/**
 * Global withdrawal limits (spec §7) and swap limits (Phase 8, D55). Saving needs a 2FA code.
 * Per-user withdrawal overrides live on each user's page.
 */
import { Button, Card, CardTitle, Field, Input, Notice, Row, Skeleton, formatBps } from "@custos/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { PageHeader } from "../components/AdminShell";
import { PauseControl, SwapPauseControl } from "../components/controls";
import { StepUpModal } from "../components/StepUpModal";
import { api } from "../lib/api";
import { errorMessage } from "../lib/errors";
import { centsToInput, DOLLARS, inputToDollars, usd } from "../lib/format";
import { OWNER_ONLY_NOTE, useCan } from "../lib/permissions";
import { qk, useLimits } from "../lib/queries";

export function LimitsPage() {
  const limits = useLimits();
  const canEdit = useCan()("limits.edit");
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    maxPerWithdrawal: "",
    maxPer24h: "",
    approvalAbove: "",
    cooldownHours: "0",
    emailConfirmation: false,
    hotWalletCap: "",
  });
  const p = limits.data?.policy;

  useEffect(() => {
    if (!p) return;
    setForm({
      maxPerWithdrawal: centsToInput(p.maxPerWithdrawalUsdCents),
      maxPer24h: centsToInput(p.maxPer24hUsdCents),
      approvalAbove: centsToInput(p.approvalAboveUsdCents),
      cooldownHours: String(p.whitelistCooldownHours),
      emailConfirmation: p.requireEmailConfirmation,
      hotWalletCap: centsToInput(p.hotWalletCapUsdCents),
    });
  }, [p]);

  const dollarsOk = (v: string, required = false) => {
    const d = inputToDollars(v);
    return d === null ? !required : DOLLARS.test(d);
  };
  const hours = Number(form.cooldownHours);
  const valid =
    dollarsOk(form.maxPerWithdrawal) &&
    dollarsOk(form.maxPer24h) &&
    dollarsOk(form.approvalAbove, true) &&
    dollarsOk(form.hotWalletCap) &&
    Number.isInteger(hours) &&
    hours >= 0 &&
    hours <= 720;

  const money = (
    key: "maxPerWithdrawal" | "maxPer24h" | "approvalAbove" | "hotWalletCap",
    labelText: string,
    hint: string,
  ) => (
    <Field
      label={labelText}
      hint={hint}
      error={dollarsOk(form[key], key === "approvalAbove") ? null : "Use a dollar amount like 50 or 12.50"}
    >
      {(props) => (
        <Input
          {...props}
          inputMode="decimal"
          value={form[key]}
          onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        />
      )}
    </Field>
  );

  return (
    <>
      <PageHeader
        title="Limits"
        subtitle="Global withdrawal rules. The signer also enforces its own daily caps."
      />
      {limits.error && <Notice tone="danger">{errorMessage(limits.error)}</Notice>}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          {!p ? (
            <Skeleton className="h-64" />
          ) : (
            <form
              className="grid grid-cols-1 gap-4 sm:grid-cols-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (valid) setOpen(true);
              }}
              noValidate
            >
              <fieldset disabled={!canEdit} className="contents">
                {money("maxPerWithdrawal", "Max per withdrawal ($)", "Empty = unlimited")}
                {money("maxPer24h", "Max per user per 24 h ($)", "Empty = unlimited")}
                {money("approvalAbove", "Admin approval above ($)", "Required")}
                {money("hotWalletCap", "Hot wallet cap per chain ($)", "Empty = no cap")}
                <Field label="New address cooldown (hours)" hint="0–720">
                  {(props) => (
                    <Input
                      {...props}
                      inputMode="numeric"
                      value={form.cooldownHours}
                      onChange={(e) => setForm({ ...form, cooldownHours: e.target.value.replace(/\D/g, "") })}
                    />
                  )}
                </Field>
                <label className="flex min-h-12 cursor-pointer items-center gap-3 self-end text-sm text-fg">
                  <input
                    type="checkbox"
                    checked={form.emailConfirmation}
                    onChange={(e) => setForm({ ...form, emailConfirmation: e.target.checked })}
                    className="size-5 accent-[var(--primary)]"
                  />
                  Email confirmation link for every withdrawal
                </label>
                <div className="sm:col-span-2">
                  {canEdit ? (
                    <Button type="submit" disabled={!valid}>
                      Save limits
                    </Button>
                  ) : (
                    <p className="text-sm text-muted">{OWNER_ONLY_NOTE}</p>
                  )}
                </div>
              </fieldset>
            </form>
          )}
        </Card>
        <Card className="flex flex-col gap-3">
          <CardTitle>Now in force</CardTitle>
          {p && (
            <div data-testid="limits-now">
              <Row label="Max per withdrawal">{usd(p.maxPerWithdrawalUsdCents)}</Row>
              <Row label="Max per 24 h">{usd(p.maxPer24hUsdCents)}</Row>
              <Row label="Approval above">{usd(p.approvalAboveUsdCents)}</Row>
              <Row label="Hot wallet cap">{usd(p.hotWalletCapUsdCents, "none")}</Row>
              <Row label="Address cooldown">{p.whitelistCooldownHours} h</Row>
              <Row label="Email confirmation">{p.requireEmailConfirmation ? "yes" : "no"}</Row>
              <Row label="2FA code">always</Row>
            </div>
          )}
          <PauseControl />
        </Card>
      </div>
      <SwapLimits />
      <StepUpModal
        open={open}
        onOpenChange={setOpen}
        title="Save new limits?"
        description="They apply to every new withdrawal immediately."
        confirmLabel="Save limits"
        onConfirm={(code) =>
          api.setLimits(
            {
              maxPerWithdrawal: inputToDollars(form.maxPerWithdrawal),
              maxPer24h: inputToDollars(form.maxPer24h),
              approvalAbove: inputToDollars(form.approvalAbove)!,
              cooldownHours: hours,
              emailConfirmation: form.emailConfirmation,
              hotWalletCap: inputToDollars(form.hotWalletCap),
            },
            code,
          )
        }
        onDone={() => queryClient.invalidateQueries({ queryKey: qk.limits })}
      />
    </>
  );
}

function SwapLimits() {
  const limits = useLimits();
  const canEdit = useCan()("limits.edit");
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [maxPerSwap, setMaxPerSwap] = useState("");
  const [feeBps, setFeeBps] = useState("0");
  const s = limits.data?.swaps;

  useEffect(() => {
    if (!s) return;
    setMaxPerSwap(centsToInput(s.policy.maxPerSwapUsdCents));
    setFeeBps(String(s.policy.feeBps));
  }, [s]);

  const maxOk = inputToDollars(maxPerSwap) === null || DOLLARS.test(inputToDollars(maxPerSwap)!);
  const fee = Number(feeBps);
  const feeOk = feeBps !== "" && Number.isInteger(fee) && fee >= 0 && fee <= 500;

  return (
    <section className="mt-6" aria-label="Swaps">
      <h2 className="mb-3 text-xl font-medium text-fg">Swaps</h2>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          {!s ? (
            <Skeleton className="h-32" />
          ) : (
            <form
              className="grid grid-cols-1 gap-4 sm:grid-cols-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (maxOk && feeOk) setOpen(true);
              }}
              noValidate
            >
              <fieldset disabled={!canEdit} className="contents">
                <Field
                  label="Max per swap ($)"
                  hint="Empty = unlimited"
                  error={maxOk ? null : "Use a dollar amount like 100 or 12.50"}
                >
                  {(props) => (
                    <Input
                      {...props}
                      inputMode="decimal"
                      value={maxPerSwap}
                      onChange={(e) => setMaxPerSwap(e.target.value)}
                    />
                  )}
                </Field>
                <Field
                  label="Swap fee (basis points)"
                  hint={feeOk ? `100 = 1%. Now: ${formatBps(fee)}. 0 = no fee.` : undefined}
                  error={feeOk ? null : "A whole number from 0 to 500"}
                >
                  {(props) => (
                    <Input
                      {...props}
                      inputMode="numeric"
                      value={feeBps}
                      onChange={(e) => setFeeBps(e.target.value.replace(/\D/g, ""))}
                    />
                  )}
                </Field>
                <div className="sm:col-span-2">
                  {canEdit ? (
                    <Button type="submit" disabled={!maxOk || !feeOk}>
                      Save swap limits
                    </Button>
                  ) : (
                    <p className="text-sm text-muted">{OWNER_ONLY_NOTE}</p>
                  )}
                </div>
              </fieldset>
            </form>
          )}
        </Card>
        <Card className="flex flex-col gap-3">
          <CardTitle>Swaps now</CardTitle>
          {s && (
            <div data-testid="swap-limits-now">
              <Row label="Max per swap">{usd(s.policy.maxPerSwapUsdCents)}</Row>
              <Row label="Fee">{s.policy.feeBps === 0 ? "none" : formatBps(s.policy.feeBps)}</Row>
              <Row label="2FA code">not needed (stays in the account)</Row>
            </div>
          )}
          <SwapPauseControl />
        </Card>
      </div>
      <StepUpModal
        open={open}
        onOpenChange={setOpen}
        title="Save swap limits?"
        description="They apply to every new quote immediately."
        confirmLabel="Save swap limits"
        onConfirm={(code) => api.setSwapLimits({ maxPerSwap: inputToDollars(maxPerSwap), feeBps: fee }, code)}
        onDone={() =>
          Promise.all([
            queryClient.invalidateQueries({ queryKey: qk.limits }),
            queryClient.invalidateQueries({ queryKey: qk.overview }),
          ])
        }
      />
    </section>
  );
}
